import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQProps {
  items?: FAQItem[];
  question?: string;
  answer?: string;
  title?: string;
  subtitle?: string;
  className?: string;
}

/**
 * FAQ accordion — neumorphic theme
 */
const FAQ: React.FC<FAQProps> = ({
  items = [],
  question,
  answer,
  title = 'Frequently Asked Questions',
  subtitle,
  className = '',
}) => {
  const faqItems = question && answer ? [{ question, answer }] : items;
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className={`w-full max-w-4xl mx-auto ${className}`}>
      {title && (
        <div className="text-center mb-10">
          <h2 className="font-display text-2xl md:text-3xl font-bold mb-3 text-neu-text">{title}</h2>
          {subtitle && (
            <p className="text-neu-muted text-lg max-w-2xl mx-auto">{subtitle}</p>
          )}
        </div>
      )}

      <div className="space-y-4">
        {faqItems.map((item, index) => {
          const isOpen = openIndex === index;
          return (
            <div
              key={index}
              className="rounded-neu-sm bg-neu-bg overflow-hidden transition-all duration-300"
              style={{
                boxShadow: isOpen
                  ? 'inset 6px 6px 10px rgb(163,177,198,0.5), inset -6px -6px 10px rgba(255,255,255,0.45)'
                  : '6px 6px 12px rgb(163,177,198,0.6), -6px -6px 12px rgba(255,255,255,0.5)',
              }}
            >
              {/* Question button */}
              <button
                className="flex items-center justify-between w-full px-6 py-4 text-left focus:outline-none"
                onClick={() => setOpenIndex(isOpen ? null : index)}
                aria-expanded={isOpen}
              >
                <span className="font-display font-semibold text-base text-neu-text pr-4">{item.question}</span>
                <div
                  className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center transition-transform duration-300"
                  style={{
                    background: '#E0E5EC',
                    boxShadow: isOpen
                      ? 'inset 3px 3px 6px rgb(163,177,198,0.6), inset -3px -3px 6px rgba(255,255,255,0.5)'
                      : '3px 3px 6px rgb(163,177,198,0.6), -3px -3px 6px rgba(255,255,255,0.5)',
                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    color: '#6C63FF',
                  }}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>

              {/* Answer */}
              <AnimatePresence>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-5 text-neu-muted text-sm leading-relaxed">
                      <div
                        className="pt-3"
                        style={{ borderTop: '1px solid rgba(163,177,198,0.35)' }}
                      >
                        {item.answer}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const FAQList: React.FC<{ faqs?: FAQItem[] }> = ({ faqs = [] }) => (
  <FAQ items={faqs} title="" />
);

export default FAQ;
